import React, { useMemo, useState, useCallback } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
    Col,
    Row,
    UncontrolledTooltip,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    FormFeedback,
    Label,
    Card,
    CardBody,
    Button,
} from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
    useTransmissions,
    useCreateTransmission,
    useEditTransmission,
    useDeleteTransmission,
} from "hooks/transmissionHook"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

const TransmissionList = () => {
  document.title = "Transmission List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [transmission, setTransmission] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: transmissionsResponse,
    isLoading,
    refetch: transmissionRefetch,
    isRefetching: transmissionRefetchLoading,
  } = useTransmissions(queryParams)

  const { mutateAsync: createTransmission, isPending: createLoading } =
    useCreateTransmission()
  const { mutateAsync: editTransmission, isPending: editLoading } =
    useEditTransmission()
  const { mutateAsync: deleteTransmission, isPending: deleteLoading } =
    useDeleteTransmission()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setTransmission(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setTransmission(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleTransmissionClick = useCallback(
    arg => {
      setTransmission({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(data => {
    setTransmission(data)
    setDeleteModal(true)
  }, [])

  const handleDeleteTransmission = useCallback(async () => {
    if (transmission?.id) {
      try {
        const res = await deleteTransmission(transmission.id)
        toast.success(res?.message ?? "Transmission deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        toast.error("Failed to delete transmission.")
      }
    }
  }, [transmission, deleteTransmission])

  const handleRefresh = () => transmissionRefetch()

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .required("Please enter transmission name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name }
      const action = isEdit
        ? editTransmission({ id: transmission.id, ...payload })
        : createTransmission(payload)
      try {
        const res = await action
        toast.success(
          res?.message ??
            `Transmission ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        toast.error("Failed to save transmission.")
      }
    },
    [isEdit, transmission, editTransmission, createTransmission, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: transmission?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleTransmissionClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleTransmissionClick, onClickDelete]
  )

  const transmissionsData = transmissionsResponse?.data?.data || []
  const paginationInfo = transmissionsResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTransmission}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Transmission"
            breadcrumbItem="Transmission List"
          />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex align-items-center">
                  <h5 className="mb-0 flex-grow-1">Transmission List</h5>
                  <div>
                    <Button
                      color="primary"
                      className="me-1"
                      onClick={handleAddNewClick}
                    >
                      Add New Transmission
                    </Button>
                    <Button
                      color="light"
                      className="me-1"
                      onClick={handleRefresh}
                      id="refreshTooltip"
                    >
                      <i
                        className={`mdi mdi-refresh ${
                          transmissionRefetchLoading ? "mdi-spin" : ""
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="refreshTooltip"
                      >
                        Refresh
                      </UncontrolledTooltip>
                    </Button>
                    {hasActiveFilters && (
                      <Button color="danger" onClick={resetParams}>
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={transmissionsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || transmissionRefetchLoading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Transmission" : "Add Transmission"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Transmission Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter transmission name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  )}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={createLoading || editLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Transmission"}
                    {(createLoading || editLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default TransmissionList
